import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiCake } from 'react-icons/hi2';
import EmptyState from '../../../../components/misc/EmptyState';
import * as connectionService from '../../../../services/connectionService';

interface BirthdayEntry {
  id: number;
  name: string;
  birthDate: string;
  age: number;
  daysUntil: number;
  picture?: string;
}

interface ConnectionWithBirthday {
  id: number;
  firstName: string;
  lastName: string;
  picture?: string;
  birthDate: string;
}

export default function BirthdaysTab(): JSX.Element {
  const { t } = useTranslation();
  const [connections, setConnections] = useState<ConnectionWithBirthday[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);

    const fetchConnectionsWithBirthdays = async () => {
      try {
        const allConnections = await connectionService.getMyConnections();

        // Fetch shared data for each connection to find birthdays
        const connectionsWithBirthdates = await Promise.all(
          allConnections.map(async (connection) => {
            try {
              const sharedData = await connectionService.getSharedDataByUser(connection.id.toString());
              const birthdateField = sharedData.find((field: any) => field.dataType === 'BIRTHDATE' && field.birthDate);

              if (birthdateField?.birthDate) {
                return {
                  id: connection.id,
                  firstName: connection.firstName,
                  lastName: connection.lastName,
                  picture: connection.picture,
                  birthDate: birthdateField.birthDate,
                };
              }

              return null;
            } catch (error) {
              console.error(`Failed to fetch shared data for connection ${connection.id}:`, error);
              return null;
            }
          }),
        );

        const validConnections = connectionsWithBirthdates.filter(
          (conn): conn is NonNullable<typeof conn> => conn !== null,
        );

        setConnections(validConnections);
      } catch (error) {
        console.error('Failed to fetch connections:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchConnectionsWithBirthdays();
  }, []);

  const birthdayEntries = useMemo(() => {
    return connections
      .map((connection): (BirthdayEntry & { nextBirthday: Date }) | null => {
        const birthDateString = connection.birthDate;

        // Parse the birthdate (format: YYYY-MM-DD)
        const [year, month, day] = birthDateString.split('-').map(Number);

        if (
          Number.isNaN(year) ||
          Number.isNaN(month) ||
          Number.isNaN(day) ||
          year < 1 ||
          month < 1 ||
          month > 12 ||
          day < 1 ||
          day > 31
        ) {
          return null;
        }

        const birthDate = new Date(year, month - 1, day);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const currentYear = today.getFullYear();
        const nextBirthday = new Date(currentYear, month - 1, day);
        nextBirthday.setHours(0, 0, 0, 0);

        if (nextBirthday < today) {
          nextBirthday.setFullYear(currentYear + 1);
        }

        let age = today.getFullYear() - birthDate.getFullYear();
        const hasHadBirthdayThisYear = new Date(currentYear, month - 1, day) <= today;
        if (!hasHadBirthdayThisYear) age -= 1;

        const daysUntil = Math.ceil((nextBirthday.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

        return {
          id: connection.id,
          name: `${connection.firstName} ${connection.lastName}`,
          birthDate: birthDateString,
          age,
          daysUntil,
          picture: connection.picture,
          nextBirthday,
        };
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
      .sort((a, b) => a.nextBirthday.getTime() - b.nextBirthday.getTime());
  }, [connections]);

  const formatDate = (dateString: string) => {
    const [year, month, day] = dateString.split('-');
    return `${day}-${month}-${year}`;
  };

  if (loading) {
    return (
      <div className="flex w-full items-center justify-center py-12">
        <p className="text-details/70">{t('general.loading')}</p>
      </div>
    );
  }

  if (birthdayEntries.length === 0) {
    return (
      <EmptyState
        icon={<HiCake className="h-16 w-16 text-primary" />}
        title={t('page.agenda.birthdays.noBirthdays')}
        subtitle={t('page.agenda.birthdays.noBirthdaysDescription')}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Mobile: card list (visible on small screens) */}
      <div className="flex flex-col gap-4 md:hidden">
        {birthdayEntries.map((entry) => (
          <div
            key={entry.id}
            className="bg-card flex items-center justify-between gap-4 rounded-lg border border-details/10 px-4 py-3 shadow-sm">
            <div className="flex items-center gap-3">
              {entry.picture ? (
                <img src={entry.picture} alt={entry.name} className="h-12 w-12 rounded-lg object-cover" />
              ) : (
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                  <HiCake className="h-6 w-6 text-primary" />
                </div>
              )}

              <div>
                <div className="font-medium text-primary">{entry.name}</div>
                <div className="text-sm text-details/70">{formatDate(entry.birthDate)}</div>
              </div>
            </div>

            <div className="flex flex-col items-end gap-2">
              <div className="text-sm text-details/80">
                {entry.daysUntil === 0 ? (
                  <span className="font-medium text-primary">
                    {t('page.agenda.birthdays.table.today', { new_age: entry.age })}
                  </span>
                ) : (
                  `${entry.age} → ${entry.age + 1}`
                )}
              </div>

              <div>
                {entry.daysUntil === 0 ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                    <HiCake className="h-4 w-4" />
                    {t('page.agenda.birthdays.today')}
                  </span>
                ) : entry.daysUntil === 1 ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                    {t('page.agenda.birthdays.tomorrow')}
                  </span>
                ) : entry.daysUntil <= 7 ? (
                  <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                    {t('page.agenda.birthdays.inDays', { days: entry.daysUntil })}
                  </span>
                ) : (
                  <span className="text-details/70">
                    {t('page.agenda.birthdays.inDays', { days: entry.daysUntil })}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop/table view (visible on md+) */}
      <div className="hidden overflow-hidden rounded-lg md:block">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-details/10 bg-secondary">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-primary">
                  {t('page.agenda.birthdays.table.name')}
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-primary">
                  {t('page.agenda.birthdays.table.birthday')}
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-primary">
                  {t('page.agenda.birthdays.table.age')}
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-primary">
                  {t('page.agenda.birthdays.table.daysUntil')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-details/10">
              {birthdayEntries.map((entry) => (
                <tr key={entry.id} className="hover:bg-secondary-100 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {entry.picture ? (
                        <img
                          src={entry.picture}
                          alt={entry.name}
                          className="h-12 w-12 flex-shrink-0 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10">
                          <HiCake className="h-6 w-6 text-primary" />
                        </div>
                      )}
                      <span className="font-medium text-primary">{entry.name}</span>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-details/80">{formatDate(entry.birthDate)}</td>
                  <td className="px-6 py-4 text-sm text-details/80">
                    {entry.daysUntil === 0 ? (
                      <span className="font-medium text-primary">
                        {t('page.agenda.birthdays.table.today', { new_age: entry.age })}
                      </span>
                    ) : (
                      `${entry.age} → ${entry.age + 1}`
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-details/80">
                    {entry.daysUntil === 0 ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                        <HiCake className="h-4 w-4" />
                        {t('page.agenda.birthdays.today')}
                      </span>
                    ) : entry.daysUntil === 1 ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                        {t('page.agenda.birthdays.tomorrow')}
                      </span>
                    ) : entry.daysUntil <= 7 ? (
                      <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                        {t('page.agenda.birthdays.inDays', { days: entry.daysUntil })}
                      </span>
                    ) : (
                      <span className="text-details/70">
                        {t('page.agenda.birthdays.inDays', { days: entry.daysUntil })}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
